import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Grid,
	Heading,
	HeadingLevel,
	Modal,
	Row,
	StyleProps,
	toGrammaticalNumber,
	toLocalDateString,
} from "@hex-insights/core";
import { Tile } from "../../../../../Components";
import {
	AttendanceRecordCountStatistics,
	SchoolAttendanceRecordFilterInput,
	StudentFilterInput,
	useSchoolAttendanceRecordCountStatisticsQuery,
} from "../../../../../Utilities";
import { AttendanceRatingBadge } from "../../../_General";
import {
	SchoolAttendanceRecordDonutChart,
	SchoolAttendanceRecordDonutChartLegend,
	statisticsToDonutChartData,
} from "../../../_General/Attendance/Reports/Displays";
import { SchoolAttendanceRecordReportModal } from "../Modal";
import styles from "./styles.module.css";

export type SchoolAttendanceRecordReportTileProps = {
	date?: string;
	schoolAttendanceRecordFilters?: SchoolAttendanceRecordFilterInput;
	studentFilters?: StudentFilterInput;
	headingLevel?: HeadingLevel;
} & Partial<ClassNameProps & StyleProps>;

export function SchoolAttendanceRecordReportTile({
	date = toLocalDateString(new Date()),
	schoolAttendanceRecordFilters,
	studentFilters,
	headingLevel = 2,
	className,
	style,
}: SchoolAttendanceRecordReportTileProps) {
	const { data } = useSchoolAttendanceRecordCountStatisticsQuery({
		variables: {
			date,
			schoolAttendanceRecordFilters,
			studentFilters,
		},
		fetchPolicy: "cache-and-network",
	});

	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<Tile className={className} style={style}>
			<Tile.Header>
				<Row justify="space-between">
					<Heading level={headingLevel} noMargin>
						Attendance
					</Heading>

					<Button variant="link" size="small" onClick={openModal}>
						View
					</Button>
				</Row>
			</Tile.Header>
			<Tile.Body>
				<Conditional>
					<SchoolAttendanceRecordReportSummaryView statistics={data?.schoolAttendanceRecordCountStatistics} />
				</Conditional>

				<Modal.If condition={isModalOpen}>
					<SchoolAttendanceRecordReportModal
						initialDate={date}
						studentFilters={studentFilters}
						schoolAttendanceRecordFilters={schoolAttendanceRecordFilters}
						onClose={closeModal}
					/>
				</Modal.If>
			</Tile.Body>
		</Tile>
	);
}

export type SchoolAttendanceRecordReportSummaryViewProps = {
	statistics:
		| Pick<
				AttendanceRecordCountStatistics,
				| "students"
				| "present"
				| "presentTotal"
				| "absentTotal"
				| "lateArrivalTotal"
				| "earlyDepartureTotal"
				| "notTaken"
		  >
		| null
		| undefined;
	chartCenterValue?: number;
	chartCenterLabel?: string;
};

export function SchoolAttendanceRecordReportSummaryView({
	statistics,
	chartCenterValue,
	chartCenterLabel,
}: SchoolAttendanceRecordReportSummaryViewProps) {
	const { chartData, numStudents } = React.useMemo(
		() => ({
			chartData: statisticsToDonutChartData(statistics),
			numStudents: statistics?.students ?? 0,
		}),
		[statistics],
	);

	return (
		<Column justify="spaced-start">
			<Row justify="spaced-start" align="center" horizontalSpacing="1.5rem">
				<SchoolAttendanceRecordDonutChart
					data={chartData}
					centerValue={chartCenterValue ?? numStudents}
					centerLabel={chartCenterLabel ?? toGrammaticalNumber("Student", numStudents)}
				/>
				<SchoolAttendanceRecordDonutChartLegend data={chartData} />
			</Row>

			<Grid tracks={3} gap="0.75rem">
				<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" className={styles["tile__statistic"]}>
					<span className={styles["tile__statistic__label"]}>Overall Rating</span>
					<AttendanceRatingBadge rating="good" />
				</Column>
				<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" className={styles["tile__statistic"]}>
					<span className={styles["tile__statistic__label"]}>Absent</span>
					<span className={styles["tile__statistic__value"]}>{statistics?.absentTotal}</span>
				</Column>
				<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" className={styles["tile__statistic"]}>
					<span className={styles["tile__statistic__label"]}>Present</span>
					<span className={styles["tile__statistic__value"]}>{statistics?.presentTotal}</span>
				</Column>
			</Grid>
		</Column>
	);
}
